import React, {
  ForwardedRef,
  forwardRef,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import { Button, Checkbox, CheckboxProps, CheckboxRef, Divider } from 'antd';
import styled from 'styled-components';
import { CheckboxGroupProps } from 'antd/es/checkbox/Group';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { FocusRef } from '../../components';
import { useGetIdFromName } from '../../form';
import translations from './CheckboxPicker.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'table-filter-checkbox-picker'
);
addTranslations(translations);

export interface CheckboxPickerProps<
  T extends string | number | boolean = number
> {
  name?: string;
  options: { id: T; name: string }[];
  value: T[];
  onChange: (values: T[]) => void;
  onClear?: () => any;
  maxWidth?: string | number;
}

function CheckboxPickerInner<T extends string | number | boolean = number>(
  props: CheckboxPickerProps<T>,
  ref?: ForwardedRef<FocusRef>
): ReactNode {
  const { options, value, onChange, onClear, maxWidth } = props;
  const { t } = useTranslation();
  const getIdFromName = useGetIdFromName();

  return (
    <>
      <StyledCheckboxGroup
        value={value}
        onChange={onChange as CheckboxGroupProps['onChange']}
        $maxWidth={maxWidth}
      >
        {options?.length
          ? options.map((option, index) => (
              <PickerCheckbox
                key={option.name}
                ref={ref}
                value={value}
                name={props.name}
                index={index}
                option={option}
              />
            ))
          : t('no-options')}
      </StyledCheckboxGroup>
      {!!options?.length && onClear && (
        <>
          <ClearFiltersButtonDivider />
          <Button
            id={getIdFromName(`${props.name}-clear`)}
            type="link"
            disabled={!value?.length}
            onClick={onClear}
          >
            {t('button.clear')}
          </Button>
        </>
      )}
    </>
  );
}

interface PickerCheckboxProps {
  name?: string;
  index: number;
  value: (string | number | boolean)[];
  option: { id: string | number | boolean; name: string };
}

const PickerCheckbox = forwardRef<FocusRef, PickerCheckboxProps>(
  (props, forwardedRef) => {
    const { option, index, value } = props;
    const getIdFromName = useGetIdFromName();
    const id = getIdFromName(`${props.name}-${option.id}`);
    const internalRef = useRef<CheckboxRef>(null);

    // This effect is used to automatically check the checkbox in case checkbox value
    // is an array and checkbox group value contains all these array items.
    // Antd Checkbox.Group does not support this out of the box.
    useEffect(
      () => {
        if (
          Array.isArray(option.id) &&
          Array.isArray(value) &&
          option.id.every((v) => value.includes(v))
        ) {
          const checkboxRef =
            index === 0 && typeof forwardedRef === 'object'
              ? (forwardedRef?.current as CheckboxRef)
              : internalRef.current;
          const input = checkboxRef?.input;

          if (input) {
            setTimeout(() => {
              input.click();
            });
          }
        }
      },
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
      []
    );

    return index === 0 && forwardedRef && props.name ? (
      <FocusedCheckbox id={id} key={id} ref={forwardedRef} value={option.id}>
        {option.name}
      </FocusedCheckbox>
    ) : (
      <Checkbox key={id} id={id} ref={internalRef} value={option.id}>
        {option.name}
      </Checkbox>
    );
  }
);

export const CheckboxPicker = forwardRef(CheckboxPickerInner);

type FocusedCheckboxProps = Omit<CheckboxProps, 'id'> & { id: string };

const FocusedCheckbox = forwardRef<FocusRef, FocusedCheckboxProps>(
  (props, ref) => {
    return <Checkbox {...props} ref={ref as ForwardedRef<CheckboxRef>} />;
  }
);

const StyledCheckboxGroup = styled(Checkbox.Group)<{
  $maxWidth?: string | number;
}>`
  row-gap: 8px;
  max-width: ${({ $maxWidth = '20em' }) =>
    typeof $maxWidth === 'number' ? `${$maxWidth}px` : $maxWidth};
`;

const ClearFiltersButtonDivider = styled(Divider)`
  margin: 0;
  border-color: rgba(0, 0, 0, 0.1);
`;
